import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { UhaUserContext } from '../../../user/UhaUserStore';
import { StudiestedApiContext } from '../../../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../../../api/PraksisstedApiContext';
import { HttpError } from '../../../api/generated/studiested-generated-api';
import { NotificationFunctionContext } from '../../../components/notifications/notificationProvider';
import Spinner from '../../../components/spinner/spinner';
import Icon, { IconType } from '../../../components/icon';
import * as css from '../practiceplaceprofile.module.scss';
import translations from '../practiceplaceprofile.i18n';
import SingleFileUpload, {
  fileMIMETypesToFiletypes,
  LabelUploadLink,
} from '../../../components/fileupload';
import { blobToDataURL, fetchStringContentBlob } from '../../../util/download';

type BildeFile = {
  id: string | undefined;
  file: File | undefined;
  dataUrl: string | undefined;
};

const useBilde = (
  praksisstedId: string,
  bildeFileId: string | undefined,
  filename: string,
): {
  bilde: BildeFile | undefined;
  loading: boolean;
  handleFileChange: (file?: File) => void;
} => {
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );
  const { uhaUser } = useContext(UhaUserContext);
  const { praksisstedApi: UHAPraksisstedApi } =
    useContext(StudiestedApiContext);
  const { praksisstedApi: PSAPraksisstedApi } = useContext(
    PraksisstedApiContext,
  );

  const praksisstedApi = uhaUser ? UHAPraksisstedApi : PSAPraksisstedApi;

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      bildeLagret: formatMessage({ id: translations.bildeLagret.id }),
      bildeSlettet: formatMessage({ id: translations.bildeSlettet.id }),
    }),
    [formatMessage],
  );

  const [bilde, setBilde] = useState<BildeFile>({
    id: bildeFileId,
    file: undefined,
    dataUrl: undefined,
  });
  const { id: bildeId } = bilde;

  const [loadingGet, setLoadingGet] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);

  useEffect(() => {
    if (bildeFileId) {
      setLoadingGet(true);

      praksisstedApi
        .praksisstedprofilIdBildeBildeIdGet({
          pathParams: { id: praksisstedId, bildeId: bildeFileId },
        })
        .then((url) => {
          fetchStringContentBlob(url)
            .then((bildeBlob) => {
              setBilde({
                id: bildeId,
                file: new File([bildeBlob], filename, {
                  type: bildeBlob.type,
                }),
                dataUrl: url,
              });
            })
            .finally(() => {
              setLoadingGet(false);
            });
        });
    }
    // Only rerender if bildeFileId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bildeFileId]);

  const handleDelete = () => {
    if (!bildeId) return;

    praksisstedApi
      .praksisstedprofilIdBildeBildeIdDelete({
        pathParams: { id: praksisstedId, bildeId },
      })
      .then(() => {
        setBilde({ id: undefined, file: undefined, dataUrl: undefined });
        addSuccessNotification(labels.bildeSlettet);
      })
      .catch((err: HttpError) => {
        addErrorNotification(err.body);
      });
  };

  const handleFileChange = (file?: File) => {
    if (file) {
      setLoadingPost(true);

      blobToDataURL(file).then((dataUrl) => {
        praksisstedApi
          .praksisstedprofilIdBildePost({
            pathParams: { id: praksisstedId },
            body: dataUrl,
          })
          .then((newBildeId) => {
            setBilde({ id: newBildeId, file, dataUrl });
            addSuccessNotification(labels.bildeLagret);
          })
          .catch((err: HttpError) => {
            addErrorNotification(err.body);
          })
          .finally(() => {
            setLoadingPost(false);
          });
      });
    } else {
      handleDelete();
    }
  };

  return { bilde, loading: loadingGet || loadingPost, handleFileChange };
};

interface BildeProps {
  bildeFileId?: string;
  bildeFilename: string;
  praksisstedId: string;
  isEditMode: boolean;
}

export const Bilde: React.FC<React.PropsWithChildren<BildeProps>> = ({
  bildeFileId,
  bildeFilename,
  praksisstedId,
  isEditMode,
}) => {
  const acceptImageMIMETypes = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/bmp',
  ];
  const acceptImageMaxSizeInMB = 10;
  const recommendedImageAspectRatio = '16:9';

  const { bilde, loading, handleFileChange } = useBilde(
    praksisstedId,
    bildeFileId,
    bildeFilename,
  );

  return (
    <div
      className={classnames(css.pictureContainer)}
      data-cy="pictureContainer"
    >
      <ProfileBilde isEditMode={isEditMode} data={bilde?.dataUrl} />
      {loading ? (
        <Spinner height="448px" />
      ) : (
        isEditMode && (
          <SingleFileUpload
            accept={acceptImageMIMETypes.toString()}
            maxSizeInMB={acceptImageMaxSizeInMB}
            label={
              <ProfileBildeUploadLabel
                acceptImageMIMETypes={acceptImageMIMETypes}
                acceptImageMaxSizeInMB={acceptImageMaxSizeInMB}
                recommendedImageAspectRatio={recommendedImageAspectRatio}
              />
            }
            onChange={handleFileChange}
            className={css.pictureFileUpload}
            initialFile={bilde?.file}
          />
        )
      )}
    </div>
  );
};

interface ProfileBildeProps {
  isEditMode: boolean;
  data?: string;
}

const ProfileBilde: React.FC<React.PropsWithChildren<ProfileBildeProps>> = ({
  isEditMode,
  data,
}) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      profileImgAltText: formatMessage({
        id: translations.profileImgAltText.id,
      }),
    }),
    [formatMessage],
  );

  const placeholderPicture = !isEditMode ? (
    <div className={css.profilePictureReplacement}>
      <div className={css.iconWrapper}>
        <Icon type={IconType.EndNode} title={''} />
      </div>
    </div>
  ) : (
    <></>
  );

  return data ? (
    <img
      src={data}
      className={classnames(css.profilePicture, isEditMode && css.editMode)}
      alt={labels.profileImgAltText}
    />
  ) : (
    placeholderPicture
  );
};

interface ProfileBildeUploadLabelProps {
  acceptImageMIMETypes: string[];
  acceptImageMaxSizeInMB: number;
  recommendedImageAspectRatio: string;
}

const ProfileBildeUploadLabel: React.FC<
  React.PropsWithChildren<ProfileBildeUploadLabelProps>
> = ({
  acceptImageMIMETypes,
  acceptImageMaxSizeInMB,
  recommendedImageAspectRatio,
}) => {
  const acceptImageFormats = acceptImageMIMETypes
    ? fileMIMETypesToFiletypes(acceptImageMIMETypes).join(', ')
    : '';
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      profileImgUploadLabelPrefix: formatMessage({
        id: translations.profileImgUploadLabelPrefix.id,
      }),
      profileImgUploadLabelLink: formatMessage({
        id: translations.profileImgUploadLabelLink.id,
      }),
      profileImgMaxSizeDescription: formatMessage(
        { id: translations.profileImgMaxSizeDescription.id },
        { maxSize: acceptImageMaxSizeInMB },
      ),
      profileImgFormatsDescription: formatMessage(
        { id: translations.profileImgFormatsDescription.id },
        { filetypes: acceptImageFormats },
      ),
      profileImgAspectRatioDescription: formatMessage(
        { id: translations.profileImgAspectRatioDescription.id },
        { aspectRatio: recommendedImageAspectRatio },
      ),
    }),
    [
      formatMessage,
      acceptImageFormats,
      acceptImageMaxSizeInMB,
      recommendedImageAspectRatio,
    ],
  );

  return (
    <p className={css.pictureUploadLabel}>
      <span>{labels.profileImgUploadLabelPrefix}</span>
      <LabelUploadLink uploadLinkLabel={labels.profileImgUploadLabelLink} />
      <span className={css.pictureDescriptionMargin}>
        {labels.profileImgMaxSizeDescription}
      </span>
      <span>{labels.profileImgFormatsDescription}</span>
      <span>{labels.profileImgAspectRatioDescription}</span>
    </p>
  );
};

export default Bilde;
